import { Button, Collapse, Icon, Input, MAGNETIC_TOKENS, Menu, Textarea, Toggle } from '@appd/uik';
import { useEffect, useState } from 'react';
import { QueryBuilderSelector } from './QueryBuilderSelector';
import {
  getAllAttributesByEntity,
  getAllEntities,
  getAllMetricTypesByEntity,
  getEntityNameFromFullName,
} from './schemaDao';
import {
  ASSISTED_QUERY_BUILDER,
  DEFAULT_ALIAS,
  EDITOR_MODES,
  LIMITS,
  METRIC_SELECTOR,
  QUERY_BOX,
  SUPPORTED_DATA_SOURCES,
} from './DataPanelConstants';
import { FETCH_FIELDS } from './DataPanelConstants';
import { QueryBuilderIdSelector } from './QueryBuilderIdSelector';
import { QueryBuilderCountSelector } from './QueryBuilderCountSelector';
import { QueryBuilderAttributeSelector } from './QueryBuilderAttributeSelector';
import { QueryBuilderMetricSelector } from './QueryBuilderMetricSelector';
import { keys, values } from 'lodash';
import {
  getMetricContent,
  getMetricFieldsByFullName,
  getMetricSourceByFullName,
  getMetricTypeByFullName,
} from './QueryBuilderUtils';
import { Datasource } from '../../../../models';
import { QueryBuilderLimitSelector } from './QueryBuilderLimitSelector';
import { QueryBuilderOrderSelector } from './QueryBuilderOrderSelector';

interface QueryBuilderProps {
  index: number;
  name: string;
  schema: any;
  dataSource: Datasource;
  onChanged: (index: number, name: string, query: Datasource) => void;
  onDelete: (name: string) => void;
  onInspect: (queryKey: string) => void;
}

const {
  COLORS: { DEFAULT_BG, DEFAULT_TEXT, CONTROL_BORDER, INTERACT_BG, INTERACT_TEXT_WEAK },
  PARTICLE: { COLOR_SCHEME },
  TYPOGRAPHY: { BODY, P3_EMPHASIS },
} = MAGNETIC_TOKENS;

export const QueryBuilder = ({
  index,
  name,
  schema,
  dataSource,
  onChanged,
  onDelete,
  onInspect,
}: QueryBuilderProps): JSX.Element => {
  const [queryBuilderKey, setQueryBuilderKey] = useState<string>('Query-Builder-' + Date.now());
  const [dataSourceOptions, setDataSourceOptions] = useState<Datasource>(dataSource);
  const [activeTab, setActiveTab] = useState(
    dataSource.options.editorMode != EDITOR_MODES.QUERY ? ASSISTED_QUERY_BUILDER : QUERY_BOX,
  );
  const [fromEntities, setFromEntities] = useState<string[]>((dataSource.options.fromEntities ?? []) as string[]);
  const [allAttributes, setAllAttributes] = useState(
    getAllAttributesByEntity((dataSource.options.fromEntities ?? []) as string[], schema),
  );
  const [allMetricTypes, setAllMetricTypes] = useState(
    getAllMetricTypesByEntity((dataSource.options.fromEntities ?? []) as string[], schema),
  );
  const [fetchItems, setFetchItems] = useState(dataSource.options.fetchItems);
  const [limits, setLimits] = useState<string[]>((dataSource.options.limits as string[]) ?? []);
  const [order, setOrder] = useState<string>((dataSource.options.order as string) ?? '');
  const [title, setTitle] = useState(name);
  const [queryBox, setQueryBox] = useState(dataSource.options.rawStr as string);

  const allEntities = getAllEntities(schema);

  const getFetchTypeFromItem = (item: string) => {
    if (item.includes(FETCH_FIELDS.ATTRIBUTES)) {
      return FETCH_FIELDS.ATTRIBUTES;
    } else if (item.includes(FETCH_FIELDS.METRICS)) {
      return FETCH_FIELDS.METRICS;
    } else if (item.includes(FETCH_FIELDS.ID)) {
      return FETCH_FIELDS.ID;
    } else if (item.includes(FETCH_FIELDS.COUNT)) {
      return FETCH_FIELDS.COUNT;
    } else if (item.includes(FETCH_FIELDS.EVENTS)) {
      return FETCH_FIELDS.EVENTS;
    } else {
      return FETCH_FIELDS.UNSUPPORTED;
    }
  };

  const needValueSelector = (field: FETCH_FIELDS) => {
    return [FETCH_FIELDS.ATTRIBUTES, FETCH_FIELDS.METRICS, FETCH_FIELDS.EVENTS].indexOf(field) >= 0;
  };

  const isUniqueField = (field: FETCH_FIELDS) => {
    return [FETCH_FIELDS.ID, FETCH_FIELDS.COUNT].indexOf(field) >= 0;
  };

  const getDefaultAliasByField = (field: FETCH_FIELDS): DEFAULT_ALIAS => {
    switch (field) {
      case FETCH_FIELDS.ID:
        return DEFAULT_ALIAS.ID;
      case FETCH_FIELDS.COUNT:
        return DEFAULT_ALIAS.COUNT;
      case FETCH_FIELDS.ATTRIBUTES:
        return DEFAULT_ALIAS.ATTRIBUTES;
      case FETCH_FIELDS.METRICS:
        return DEFAULT_ALIAS.METRICS;
      case FETCH_FIELDS.EVENTS:
        return DEFAULT_ALIAS.EVENTS;
      default:
        return DEFAULT_ALIAS.UNSUPPORTED;
    }
  };

  let allAvailableFetchTypes = [FETCH_FIELDS.ID, FETCH_FIELDS.COUNT, FETCH_FIELDS.ATTRIBUTES, FETCH_FIELDS.METRICS];

  const initialFetchFields = values(dataSource.options.fetchItems).map((item: string) => {
    const fetch = getFetchTypeFromItem(item);
    if (isUniqueField(fetch)) {
      allAvailableFetchTypes.splice(allAvailableFetchTypes.indexOf(fetch), 1);
    }
    return fetch;
  });

  const initialFetchFieldsAlias = keys(dataSource.options.fetchItems);

  const initialFetchFieldsContent = values(dataSource.options.fetchItems).map((item: string, i: number) => {
    if (needValueSelector(initialFetchFields[i])) {
      if (initialFetchFields[i] == FETCH_FIELDS.METRICS) {
        return getMetricContent(item);
      }
      const contentSearch = item.match(/\((.*?)\)/);
      if (contentSearch) return contentSearch[1];
    } else {
      return '';
    }
  });

  const [fetchFields, setFetchFields] = useState(initialFetchFields);

  const [fetchFieldsCanBeAdded, setFetchFieldsCanBeAdded] = useState(allAvailableFetchTypes as FETCH_FIELDS[]);

  const [fetchFieldsContent, setFetchFieldsContent] = useState(initialFetchFieldsContent as string[]);

  const [fetchFieldsAlias, setFetchFieldsAlias] = useState(initialFetchFieldsAlias as string[]);

  useEffect(() => {
    let res: Record<string, string> = {};
    fetchFieldsAlias.forEach((alias: string, i: number) => {
      if (fetchFields[i] == FETCH_FIELDS.METRICS) {
        res[alias] = fetchFields[i] + '(' + getMetricTypeByFullName(fetchFieldsContent[i]);
        if (getMetricSourceByFullName(fetchFieldsContent[i]) != METRIC_SELECTOR.ALLSOURCES) {
          res[alias] += ', "' + getMetricSourceByFullName(fetchFieldsContent[i]) + '"';
        }
        res[alias] += ')';
        if (getMetricFieldsByFullName(fetchFieldsContent[i]) != '') {
          res[alias] += getMetricFieldsByFullName(fetchFieldsContent[i]);
        }
      } else {
        res[alias] = fetchFields[i] + '(' + fetchFieldsContent[i] + ')';
      }
    });
    setFetchItems(res);
  }, [fetchFieldsAlias, fetchFields, fetchFieldsContent]);

  useEffect(() => {
    setAllAttributes(getAllAttributesByEntity(getEntityNameFromFullName(fromEntities), schema));
    setAllMetricTypes(getAllMetricTypesByEntity(getEntityNameFromFullName(fromEntities), schema));
    setDataSourceOptions({
      ...dataSourceOptions,
      options: {
        ...dataSourceOptions.options,
        editorMode: activeTab == ASSISTED_QUERY_BUILDER ? EDITOR_MODES.BUILDER : EDITOR_MODES.QUERY,
        fromEntities: fromEntities,
        fetchItems: fetchItems,
        order: order,
        limits: limits,
        rawStr: queryBox,
      },
    });
  }, [fromEntities, fetchItems, order, limits, queryBox]);

  useEffect(() => {
    onChanged(index, title, dataSourceOptions);
  }, [title, dataSourceOptions]);

  const createMenuTrigger = (title: string) => {
    return (
      <div className="flex flex-row items-center justify-between mt-2">
        <div className="flex flex-row items-center justify-between ">
          <div className="flex flex-row rounded-md border-2 border-[#1d69cc] text-[#1d69cc] px-[7px] py-[4px] mr-2">
            <Icon.Action.Add size="SMALL" />
            <button>{title}</button>
          </div>
        </div>
      </div>
    );
  };

  const { Option } = Menu;

  const removeCurrentSelector = (index: number) => {
    let newFetchFields = [...fetchFields];
    let newFetchFieldsAlias = [...fetchFieldsAlias];
    let newFetchFieldsContent = [...fetchFieldsContent];
    let newFetchFieldsCanBeAdded = [...fetchFieldsCanBeAdded];
    if (isUniqueField(fetchFields[index])) {
      newFetchFieldsCanBeAdded.push(...newFetchFields.splice(index, 1));
      setFetchFieldsCanBeAdded(newFetchFieldsCanBeAdded);
    }
    newFetchFields.splice(index, 1);
    newFetchFieldsAlias.splice(index, 1);
    newFetchFieldsContent.splice(index, 1);
    setFetchFieldsAlias(newFetchFieldsAlias);
    setFetchFields(newFetchFields);
    setFetchFieldsContent(newFetchFieldsContent);
  };

  const handleAddBack = (i: number) => {
    let newFetchFields = [...fetchFields];
    let newFetchFieldsAlias = [...fetchFieldsAlias];
    let newFetchFieldsContent = [...fetchFieldsContent];
    newFetchFieldsAlias.push('new' + getDefaultAliasByField(fetchFieldsCanBeAdded[i]) + newFetchFieldsAlias.length);
    setFetchFieldsAlias(newFetchFieldsAlias);
    newFetchFieldsContent.push('');
    setFetchFieldsContent(newFetchFieldsContent);

    if (isUniqueField(fetchFieldsCanBeAdded[i])) {
      let newFetchFieldsCanBeAdded = [...fetchFieldsCanBeAdded];
      newFetchFields.push(...newFetchFieldsCanBeAdded.splice(i, 1));
      setFetchFieldsCanBeAdded(newFetchFieldsCanBeAdded);
    } else {
      newFetchFields.push(fetchFieldsCanBeAdded[i]);
    }
    setFetchFields(newFetchFields);
  };

  const handleFetchFieldChange = (value: string, i: number) => {
    let newFetchFieldsContent = [...fetchFieldsContent];
    newFetchFieldsContent[i] = value;
    setFetchFieldsContent(newFetchFieldsContent);
  };

  const handleMetricTypeChange = (value: string, i: number) => {
    if (fetchFieldsContent[i].indexOf(',') != -1) {
      let newMetric = fetchFieldsContent[i].split(',');
      newMetric[0] = value;
      handleFetchFieldChange(newMetric.join(', '), i);
    } else {
      handleFetchFieldChange([value, METRIC_SELECTOR.ALLSOURCES].join(', '), i);
    }
  };
  const handleMetricSourceChange = (value: string, i: number) => {
    if (fetchFieldsContent[i].indexOf(',') != -1) {
      let newMetric = fetchFieldsContent[i].split(',');
      newMetric[1] = "'" + value + "'";
      handleFetchFieldChange(newMetric.join(', '), i);
    } else {
      handleFetchFieldChange([fetchFieldsContent[i], value].join(', '), i);
    }
  };
  const handleFieldsChange = (value: string, i: number) => {
    if (fetchFieldsContent[i].indexOf(',') != -1) {
      let newMetric = fetchFieldsContent[i].split(',');
      if (newMetric.length > 2) {
        handleFetchFieldChange([newMetric[0], newMetric[1], value].join(', '), i);
      } else {
        handleFetchFieldChange(newMetric.concat([value]).join(', '), i);
      }
    } else {
      handleFetchFieldChange([fetchFieldsContent[i], METRIC_SELECTOR.ALLSOURCES, value].join(', '), i);
    }
  };

  const handleAliasChange = (value: string, i: number) => {
    let newAlias = [...fetchFieldsAlias];
    newAlias[i] = value;
    setFetchFieldsAlias(newAlias);
  };

  const onAddLimit = () => {
    let newLimits = [...limits];
    newLimits.push(...['metrics.']);
    setLimits(newLimits);
  };

  const onLimitChange = (value: string, i: number) => {
    let newLimits = [...limits];
    newLimits[i] = value;
    setLimits(newLimits);
  };

  const removeLimitSelector = (i: number) => {
    let newLimits = [...limits];
    newLimits.splice(i, 1);
    setLimits(newLimits);
  };

  const onAddOrder = () => {
    if (fetchFieldsAlias.length > 0) {
      setOrder('ORDER topology.desc()');
    }
  };

  return (
    <Collapse>
      <Collapse.Header>
        <div className="tabs my-2 flex flex-row items-center justify-between">
          <div className="text-sm" onClick={e => e.stopPropagation()}>
            <Input
              className="px-2 border-2 rounded-md truncate"
              style={{ borderColor: CONTROL_BORDER }}
              onChange={e => setTitle(e.target.value)}
              placeholder={'Enter Query Title'}
              value={title}
            />
          </div>
          <div className="flex flex-row" onClick={e => e.stopPropagation()}>
            <Button type="SECONDARY" onClick={() => onInspect(title)}>
              Inspect
            </Button>
            <button className="px-2">
              <Icon.Action.Copy size="MEDIUM" />
            </button>
            <button className="pr-2" onClick={() => onDelete(title)}>
              <Icon.Action.Delete size="MEDIUM" />
            </button>
            <button className="pr-2">
              <Icon.Navigation.MoreHorizontal size="MEDIUM" />
            </button>
          </div>
        </div>
      </Collapse.Header>
      <Collapse.Panel>
        <div className="tab-content mt-[-20px] w-full">
          {activeTab === ASSISTED_QUERY_BUILDER && (
            <div className="flex flex-col flex-wrap w-full">
              <div className="flex flex-row flex-wrap">
                <div className="w-[150px]">
                  <div className="text-sm leading-5 font-bold">Data Source</div>
                  <div className="flex selector-container">
                    <QueryBuilderSelector
                      value={dataSourceOptions.type}
                      placeHolder="Select Data Source"
                      options={SUPPORTED_DATA_SOURCES}
                      onChange={event =>
                        setDataSourceOptions({
                          ...dataSourceOptions,
                          type: event,
                        })
                      }
                    ></QueryBuilderSelector>
                  </div>
                </div>
                <div className="w-[150px]">
                  <div className="text-sm leading-5 font-bold">From</div>
                  <div className="flex selector-container">
                    <QueryBuilderSelector
                      value={fromEntities[0]}
                      placeHolder="Select Entity Type"
                      options={allEntities}
                      onChange={event => setFromEntities([event])}
                    ></QueryBuilderSelector>
                  </div>
                </div>
              </div>

              {fetchFields.map((field: string, i: number) => {
                if (field == FETCH_FIELDS.ID) {
                  return (
                    <div key={queryBuilderKey + ' ' + index + ' ' + i} className="flex flex-row w-full">
                      <QueryBuilderIdSelector
                        alias={fetchFieldsAlias[i]}
                        onAliasChange={(e: string) => handleAliasChange(e, i)}
                      ></QueryBuilderIdSelector>
                      <button onClick={() => removeCurrentSelector(i)}>
                        <Icon.Action.Disable />
                      </button>
                    </div>
                  );
                }
                if (field == FETCH_FIELDS.COUNT) {
                  return (
                    <div key={queryBuilderKey + ' ' + index + ' ' + i} className="flex flex-row w-full">
                      <QueryBuilderCountSelector
                        alias={fetchFieldsAlias[i]}
                        onAliasChange={(e: string) => handleAliasChange(e, i)}
                      ></QueryBuilderCountSelector>
                      <button onClick={() => removeCurrentSelector(i)}>
                        <Icon.Action.Disable />
                      </button>
                    </div>
                  );
                }
                if (field == FETCH_FIELDS.ATTRIBUTES) {
                  return (
                    <div key={queryBuilderKey + ' ' + index + ' ' + i} className="flex flex-row w-full">
                      <QueryBuilderAttributeSelector
                        alias={fetchFieldsAlias[i]}
                        value={fetchFieldsContent[i]}
                        options={allAttributes}
                        onChange={(e: string) => handleFetchFieldChange(e, i)}
                        onAliasChange={(e: string) => handleAliasChange(e, i)}
                      ></QueryBuilderAttributeSelector>
                      <button onClick={() => removeCurrentSelector(i)}>
                        <Icon.Action.Disable />
                      </button>
                    </div>
                  );
                }
                if (field == FETCH_FIELDS.METRICS) {
                  return (
                    <div key={queryBuilderKey + ' ' + index + ' ' + i} className="flex flex-row w-full">
                      <QueryBuilderMetricSelector
                        alias={fetchFieldsAlias[i]}
                        entities={fromEntities}
                        value={getMetricTypeByFullName(fetchFieldsContent[i])}
                        source={getMetricSourceByFullName(fetchFieldsContent[i])}
                        options={allMetricTypes}
                        fields={getMetricFieldsByFullName(fetchFieldsContent[i])}
                        onChange={(e: string) => handleMetricTypeChange(e, i)}
                        onAliasChange={(e: string) => handleAliasChange(e, i)}
                        onSourceChange={(e: string) => handleMetricSourceChange(e, i)}
                        onFieldsChange={(e: string) => handleFieldsChange(e, i)}
                      ></QueryBuilderMetricSelector>
                      <button onClick={() => removeCurrentSelector(i)}>
                        <Icon.Action.Disable />
                      </button>
                    </div>
                  );
                }
              })}
              {limits.map((limit: string, i: number) => {
                return (
                  <div key={queryBuilderKey + 'limits' + ' ' + index + ' ' + i} className="flex flex-row w-full">
                    <QueryBuilderLimitSelector
                      value={limit}
                      onChange={(e: string) => onLimitChange(e, i)}
                    ></QueryBuilderLimitSelector>
                    <button onClick={() => removeLimitSelector(i)}>
                      <Icon.Action.Disable />
                    </button>
                  </div>
                );
              })}
              {order != '' && fetchFieldsAlias.length > 0 && (
                <div key={queryBuilderKey + 'order'} className="flex flex-row w-full">
                  <QueryBuilderOrderSelector
                    value={order}
                    aliasArr={fetchFieldsAlias}
                    onChange={(e: string) => setOrder(e)}
                  ></QueryBuilderOrderSelector>
                  <button onClick={() => setOrder('')}>
                    <Icon.Action.Disable />
                  </button>
                </div>
              )}

              <div className="flex flex-row">
                {fromEntities[0] && fetchFieldsCanBeAdded.length > 0 && (
                  <Menu trigger={createMenuTrigger('Fetch')}>
                    {fetchFieldsCanBeAdded.map((item, i) => {
                      return (
                        <Option key={i} onClick={() => handleAddBack(i)}>
                          <button type="button">{item}</button>
                        </Option>
                      );
                    })}
                  </Menu>
                )}
                {order == '' && fetchFieldsAlias.length > 0 && (
                  <div className="flex flex-row rounded-md border-2 border-[#1d69cc] text-[#1d69cc] px-[7px] py-[4px] mr-2 mt-2">
                    <Icon.Action.Add size="SMALL" />
                    <button onClick={onAddOrder}>Order</button>
                  </div>
                )}
                {fetchFieldsAlias.length > 0 && (
                  <div className="flex flex-row rounded-md border-2 border-[#1d69cc] text-[#1d69cc] px-[7px] py-[4px] mr-2 mt-2">
                    <Icon.Action.Add size="SMALL" />
                    <button onClick={onAddLimit}>Limit</button>
                  </div>
                )}
              </div>
            </div>
          )}
          {activeTab === QUERY_BOX && (
            <div className="flex flex-col">
              <div>
                <div className="text-sm leading-5 font-bold">Data Source</div>
                <div className="flex selector-container w-40">
                  <QueryBuilderSelector
                    value={dataSourceOptions.type}
                    placeHolder="Select Data Source"
                    options={SUPPORTED_DATA_SOURCES}
                    onChange={event =>
                      setDataSourceOptions({
                        ...dataSourceOptions,
                        type: event,
                      })
                    }
                  ></QueryBuilderSelector>
                </div>
              </div>
              <div className="w-[600px]">
                <div className="text-sm leading-5 font-bold">Query</div>
                <Textarea
                  maxRows={15}
                  required
                  rows={3}
                  value={queryBox}
                  onChange={event => {
                    setQueryBox(event.target.value);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </Collapse.Panel>
    </Collapse>
  );
};
